import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  contactsDB, 
  activitiesDB, 
  notesDB, 
  tasksDB, 
  meetingsDB,
  transformContact,
  transformActivity,
  transformNote,
  transformTask,
  transformMeeting,
  initializeDatabase
} from '../lib/database';
import { isSupabaseConfigured } from '../lib/supabase';
import toast from 'react-hot-toast';

const ContactContext = createContext();

export const useContacts = () => {
  const context = useContext(ContactContext);
  if (!context) {
    throw new Error('useContacts must be used within a ContactProvider');
  }
  return context;
};

export const ContactProvider = ({ children }) => {
  const [contacts, setContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(false);
  const [dbInitialized, setDbInitialized] = useState(false);

  // Initialize database and load contacts
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const initialized = await initializeDatabase();
        setDbInitialized(initialized);

        if (initialized) {
          await loadContacts();
        } else {
          // Load mock data if Supabase not configured
          loadMockData();
        }
      } catch (error) {
        console.error('Failed to initialize:', error);
        setDbInitialized(false);
        if (!error?.displayed) {
          const message = error?.message || 'Failed to initialize database connection';
          toast.error(message);
        }
        loadMockData();
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  const loadContacts = async () => {
    try {
      const { data, error } = await contactsDB.getAll();
      
      if (error) {
        console.error('Error loading contacts:', error);
        toast.error('Failed to load contacts');
        return;
      }

      const transformedContacts = data.map(transformContact);
      setContacts(transformedContacts);
    } catch (error) {
      console.error('Error loading contacts:', error);
      toast.error('Failed to load contacts');
    }
  };

  const loadMockData = () => {
    // Use existing mock data when Supabase is not configured
    const mockContacts = [
      {
        id: '1',
        firstName: 'Sarah',
        lastName: 'Johnson',
        email: 'sarah.johnson@statefarm.com',
        phone: '+1 (555) 234-5678',
        company: 'State Farm Insurance',
        title: 'Life Insurance Agent',
        recruitmentStage: 'nurturing',
        interestLevel: 'medium',
        leadSource: 'LinkedIn Outreach',
        location: 'Dallas, TX',
        experience: '3 years',
        currentProduction: '$120K annual premium',
        background: 'Experienced insurance professional with strong client relationships.',
        status: 'active',
        dateAdded: new Date().toISOString(),
        lastContact: new Date().toISOString(),
        lastContactMethod: 'email'
      },
      {
        id: '2',
        firstName: 'Mike',
        lastName: 'Chen',
        email: 'mike.chen@allstate.com',
        phone: '+1 (555) 345-6789',
        company: 'Allstate',
        title: 'Insurance Agent',
        recruitmentStage: 'interested',
        interestLevel: 'high',
        leadSource: 'Referral',
        location: 'Houston, TX',
        experience: '5 years',
        currentProduction: '$200K annual premium',
        background: 'Top performer looking for growth opportunities.',
        status: 'active',
        dateAdded: new Date().toISOString(),
        lastContact: new Date().toISOString(),
        lastContactMethod: 'ai_call'
      },
      {
        id: '3',
        firstName: 'Jennifer',
        lastName: 'Davis',
        email: 'j.davis@liberty.com',
        phone: '+1 (555) 456-7890',
        company: 'Liberty Mutual',
        title: 'Senior Agent',
        recruitmentStage: 'lead',
        interestLevel: 'unknown',
        leadSource: 'Cold Outreach',
        location: 'Austin, TX',
        experience: '2 years',
        currentProduction: '$80K annual premium',
        background: 'New to the industry but showing promise.',
        status: 'active',
        dateAdded: new Date().toISOString(),
        lastContact: null,
        lastContactMethod: null
      }
    ];

    setContacts(mockContacts);
  };

  const addContact = async (contactData) => {
    if (!isSupabaseConfigured()) {
      // Fallback to local storage for mock mode
      const newContact = {
        id: Date.now().toString(),
        ...contactData,
        dateAdded: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        status: 'active'
      };
      setContacts(prev => [newContact, ...prev]);
      return newContact;
    }

    setLoading(true);
    try {
      const { data, error } = await contactsDB.create(contactData);
      
      if (error) {
        console.error('Error creating contact:', error);
        toast.error('Failed to create contact');
        return null;
      }

      const transformedContact = transformContact(data);
      setContacts(prev => [transformedContact, ...prev]);
      toast.success('Contact created successfully');
      return transformedContact;
    } catch (error) {
      console.error('Error creating contact:', error);
      toast.error('Failed to create contact');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateContact = async (id, updates) => {
    if (!isSupabaseConfigured()) {
      // Fallback to local state for mock mode
      let updatedContact = null;
      setContacts(prev => prev.map(contact => {
        if (contact.id === id) {
          updatedContact = { ...contact, ...updates, updatedAt: new Date().toISOString() };
          return updatedContact;
        }
        return contact;
      }));
      return updatedContact;
    }

    try {
      const { data, error } = await contactsDB.update(id, updates);

      if (error) {
        console.error('Error updating contact:', error);
        toast.error('Failed to update contact');
        return null;
      }

      const transformedContact = transformContact(data);
      setContacts(prev => prev.map(contact =>
        contact.id === id ? transformedContact : contact
      ));
      toast.success('Contact updated successfully');
      return transformedContact;
    } catch (error) {
      console.error('Error updating contact:', error);
      toast.error('Failed to update contact');
      return null;
    }
  };

  const deleteContact = async (id) => {
    if (!isSupabaseConfigured()) {
      // Fallback to local state for mock mode
      setContacts(prev => prev.filter(contact => contact.id !== id));
      setSelectedContacts(prev => prev.filter(contactId => contactId !== id));
      toast.success('Contact deleted successfully');
      return;
    }

    try {
      const { error } = await contactsDB.delete(id);
      
      if (error) {
        console.error('Error deleting contact:', error);
        toast.error('Failed to delete contact');
        return;
      }

      setContacts(prev => prev.filter(contact => contact.id !== id));
      setSelectedContacts(prev => prev.filter(contactId => contactId !== id));
      toast.success('Contact deleted successfully');
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Failed to delete contact');
    }
  };

  const bulkImportContacts = async (contactsData) => {
    setLoading(true);
    let successCount = 0;
    
    try {
      for (const contactData of contactsData) {
        const result = await addContact(contactData);
        if (result) {
          successCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully imported ${successCount} contacts`);
      } else {
        toast.error('No contacts were imported. Please check your file and try again.');
      }

      return successCount;
    } catch (error) {
      console.error('Error importing contacts:', error);
      toast.error('Failed to import contacts');
      return 0;
    } finally {
      setLoading(false);
    }
  };

  const addInteraction = async (contactId, interaction) => {
    if (!isSupabaseConfigured()) {
      // For mock mode, just update the contact's lastContact
      updateContact(contactId, {
        lastContact: new Date().toISOString(),
        lastContactMethod: interaction.type
      });
      return;
    }

    try {
      const { error } = await activitiesDB.create({
        contactId,
        type: interaction.type,
        title: interaction.title || `${interaction.type} activity`,
        description: interaction.description,
        outcome: interaction.outcome,
        priority: interaction.priority,
        details: interaction.details,
        user: interaction.user
      });

      if (error) {
        console.error('Error adding interaction:', error);
        return;
      }

      // Update contact's last contact info
      await updateContact(contactId, {
        lastContact: new Date().toISOString(),
        lastContactMethod: interaction.type
      });
    } catch (error) {
      console.error('Error adding interaction:', error);
    }
  };

  const getContactById = async (id) => {
    // First check local state
    const localContact = contacts.find(contact => contact.id === id);
    if (localContact) {
      return localContact;
    }

    if (!isSupabaseConfigured()) {
      return null;
    }

    try {
      const { data, error } = await contactsDB.getById(id);
      
      if (error) {
        console.error('Error getting contact:', error);
        return null;
      }

      return transformContact(data);
    } catch (error) {
      console.error('Error getting contact:', error);
      return null;
    }
  };

  const getContactActivities = async (contactId) => {
    if (!isSupabaseConfigured()) {
      // Return mock activities for demo purposes
      return [
        {
          id: '1',
          type: 'call_outbound_ai',
          title: 'AI Call Completed - Sarah (Initial Outreach)',
          description: 'Successful AI outreach call with positive outcome',
          timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          user: 'Sarah AI Agent',
          outcome: 'interested',
          priority: 'high',
          details: {
            duration: '4:45',
            callType: 'ai_outreach',
            disposition: 'interested',
            agentName: 'Sarah - Initial Outreach',
            aiSummary: 'Contact answered and showed genuine interest in the opportunity.',
            sentiment: 'positive',
            interestLevel: 'high',
            aiConfidence: 0.89
          }
        }
      ];
    }

    try {
      const { data, error } = await activitiesDB.getByContactId(contactId);
      
      if (error) {
        console.error('Error getting activities:', error);
        return [];
      }

      return data.map(transformActivity);
    } catch (error) {
      console.error('Error getting activities:', error);
      return [];
    }
  };

  const getContactNotes = async (contactId) => {
    if (!isSupabaseConfigured()) {
      return [];
    }

    try {
      const { data, error } = await notesDB.getByContactId(contactId);
      
      if (error) {
        console.error('Error getting notes:', error);
        return [];
      }

      return data.map(transformNote);
    } catch (error) {
      console.error('Error getting notes:', error);
      return [];
    }
  };

  const getContactTasks = async (contactId) => {
    if (!isSupabaseConfigured()) {
      return [];
    }

    try {
      const { data, error } = await tasksDB.getByContactId(contactId);
      
      if (error) {
        console.error('Error getting tasks:', error);
        return [];
      }

      return data.map(transformTask);
    } catch (error) {
      console.error('Error getting tasks:', error);
      return [];
    }
  };

  const getContactMeetings = async (contactId) => {
    if (!isSupabaseConfigured()) {
      return [];
    }

    try {
      const { data, error } = await meetingsDB.getByContactId(contactId);
      
      if (error) {
        console.error('Error getting meetings:', error);
        return [];
      }

      return data.map(transformMeeting);
    } catch (error) {
      console.error('Error getting meetings:', error);
      return [];
    }
  };

  const getFilteredContacts = () => {
    return contacts.filter(contact => {
      const matchesSearch = 
        contact.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.phone?.includes(searchTerm);
      
      const matchesFilter = filterStatus === 'all' || contact.status === filterStatus;
      
      return matchesSearch && matchesFilter;
    });
  };

  const getContactStats = () => {
    return {
      total: contacts.length,
      active: contacts.filter(c => c.status === 'active').length,
      inactive: contacts.filter(c => c.status === 'inactive').length,
      recent: contacts.filter(c => {
        const createdDate = new Date(c.dateAdded || c.createdAt);
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        return createdDate > thirtyDaysAgo;
      }).length
    };
  };

  const value = {
    contacts,
    selectedContacts,
    setSelectedContacts,
    searchTerm,
    setSearchTerm,
    filterStatus,
    setFilterStatus,
    loading,
    dbInitialized,
    addContact,
    updateContact,
    deleteContact,
    bulkImportContacts,
    addInteraction,
    getContactById,
    getContactActivities,
    getContactNotes,
    getContactTasks,
    getContactMeetings,
    getFilteredContacts,
    getContactStats,
    loadContacts
  };

  return (
    <ContactContext.Provider value={value}>
      {children}
    </ContactContext.Provider>
  );
};